import { Maximize2, X, Trash2 } from 'lucide-react'
import { widgetAPI } from '../services/api'
import { useDashboardStore } from '../stores/dashboardStore'
import toast from 'react-hot-toast'
import ErrorBoundary from './ErrorBoundary'

// Import widget implementations
import GoogleSearchWidget from './widgets/GoogleSearchWidget'
import TaskWidget from './widgets/TaskWidget'
import CalendarWidget from './widgets/CalendarWidget'
import NotesWidget from './widgets/NotesWidget'
import WeatherWidget from './widgets/WeatherWidget'
import SpotifyWidget from './widgets/SpotifyWidget'
import TVSeriesWidget from './widgets/TVSeriesWidget'
import NewMoviesWidget from './widgets/NewMoviesWidget'
import NewShowsWidget from './widgets/NewShowsWidget'
import BooksWidget from './widgets/BooksWidget'
import PhotoAlbumsWidget from './widgets/PhotoAlbumsWidget'
import BookmarksWidget from './widgets/BookmarksWidget'
import EmailWidget from './widgets/EmailWidget_NEW'
import RssWidget from './widgets/RssWidget'
import RedditWidget from './widgets/RedditWidget'
import SportsWidget from './widgets/SportsWidget'
import WindWidget from './widgets/WindWidget'
import TideWidget from './widgets/TideWidget'
import WeightTrackerWidget from './widgets/WeightTrackerWidget'
import FitnessTrackerWidget from './widgets/FitnessTrackerWidget'

// Import sandboxed widget for third-party apps
import SandboxedWidget from './SandboxedWidget'

interface WidgetProps {
  widget: any
  isEditMode: boolean
  isExpanded?: boolean
}

export default function Widget({ widget, isEditMode, isExpanded = false }: WidgetProps) {
  const { setExpandedWidget, removeWidget } = useDashboardStore()

  const handleExpand = async () => {
    try {
      if (isExpanded) {
        await widgetAPI.collapse(widget.id)
        setExpandedWidget(null)
      } else {
        await widgetAPI.expand(widget.id)
        setExpandedWidget(widget.id)
      }
    } catch (error) {
      toast.error('Failed to toggle widget')
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove this widget?')) return

    try {
      await widgetAPI.delete(widget.id)
      removeWidget(widget.id)
      toast.success('Widget removed')
    } catch (error) {
      toast.error('Failed to remove widget')
    }
  }

  const renderWidgetContent = () => {
    // Debug logging
    console.log('Widget data:', { app_slug: widget.app_slug, app_name: widget.app_name, id: widget.id })

    // Check if widget is third-party (needs sandboxing)
    // Third-party widgets are identified by is_third_party flag or absence from first-party list
    const isThirdParty = widget.is_third_party === true || widget.is_third_party === 1

    if (isThirdParty) {
      // Render in secure sandbox
      return <SandboxedWidget widget={widget} isExpanded={isExpanded} />
    }

    // First-party widgets (trusted, run in React tree)
    switch (widget.app_slug) {
      case 'google-search':
        return <GoogleSearchWidget widget={widget} isExpanded={isExpanded} />
      case 'chatgpt-search':
        return <GoogleSearchWidget widget={widget} isExpanded={isExpanded} />
      case 'claude-search':
        return <GoogleSearchWidget widget={widget} isExpanded={isExpanded} />
      case 'amazon-search':
        return <GoogleSearchWidget widget={widget} isExpanded={isExpanded} />
      case 'tasks':
        return <TaskWidget widget={widget} isExpanded={isExpanded} />
      case 'calendar':
        return <CalendarWidget widget={widget} isExpanded={isExpanded} />
      case 'notes':
        return <NotesWidget widget={widget} isExpanded={isExpanded} />
      case 'weather':
        return <WeatherWidget widget={widget} isExpanded={isExpanded} />
      case 'spotify':
        return <SpotifyWidget widget={widget} isExpanded={isExpanded} />
      case 'tv-series':
        return <TVSeriesWidget widget={widget} isExpanded={isExpanded} />
      case 'new-movies':
        return <NewMoviesWidget widget={widget} isExpanded={isExpanded} />
      case 'new-shows':
        return <NewShowsWidget widget={widget} isExpanded={isExpanded} />
      case 'books-genre':
        return <BooksWidget widget={widget} isExpanded={isExpanded} />
      case 'photo-albums':
        return <PhotoAlbumsWidget widget={widget} isExpanded={isExpanded} />
      case 'bookmarks':
        return <BookmarksWidget widget={widget} isExpanded={isExpanded} />
      case 'email':
        return <EmailWidget widget={widget} isExpanded={isExpanded} />
      case 'rss':
      case 'rss-reader':
        return <RssWidget widget={widget} isExpanded={isExpanded} />
      case 'reddit':
      case 'reddit-widget':
        return <RedditWidget widget={widget} isExpanded={isExpanded} />
      case 'sports':
      case 'sports-scores':
        return <SportsWidget widget={widget} isExpanded={isExpanded} />
      case 'wind':
        return <WindWidget widget={widget} isExpanded={isExpanded} />
      case 'tide':
        return <TideWidget widget={widget} isExpanded={isExpanded} />
      case 'weight-tracker':
        return <WeightTrackerWidget widget={widget} isExpanded={isExpanded} />
      case 'fitness-tracker':
        return <FitnessTrackerWidget widget={widget} isExpanded={isExpanded} />
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>{widget.app_name} - Coming Soon</p>
          </div>
        )
    }
  }

  return (
    <div className={`widget${isExpanded ? ' expanded' : ''}`}>
      {isEditMode && (
        <div className="widget-header">
          <h3 className="font-semibold">{widget.app_name}</h3>
          
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleDelete()
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              className="p-1.5 hover:bg-red-100 text-red-600 rounded-lg transition hover:scale-110 shadow-sm border border-red-200"
              title="Remove widget"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      )}

      <div className={`widget-content ${isEditMode ? '' : 'no-header'}`}>
        {!isEditMode && widget.allow_expand && (
          <button
            onClick={handleExpand}
            className="absolute bottom-2 right-2 p-1.5 bg-white hover:bg-gray-100 rounded shadow-sm transition z-10"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <X size={16} /> : <Maximize2 size={16} />}
          </button>
        )}
        <ErrorBoundary>
          {renderWidgetContent()}
        </ErrorBoundary>
      </div>
    </div>
  )
}

